import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { AdminUser } from '../../api/admin.api';
import { USER_ROLES } from '../../utils/constants';

type CreatePayload = {
  username: string;
  password: string;
  name: string;
  role: string;
};

type UpdatePayload = Partial<AdminUser> & { username?: string; password?: string };

interface AdminUserFormProps {
  mode: 'create' | 'edit';
  initial?: AdminUser | null;
  isSubmitting?: boolean;
  onSubmit: (data: CreatePayload | UpdatePayload) => Promise<void>;
}

export const AdminUserForm: React.FC<AdminUserFormProps> = ({
  mode,
  initial,
  isSubmitting,
  onSubmit,
}) => {
  const { control, handleSubmit } = useForm<CreatePayload | UpdatePayload>({
    defaultValues:
      mode === 'create'
        ? { username: '', password: '', name: '', role: USER_ROLES.ADMIN }
        : {
            username: initial?.username ?? '',
            name: initial?.name ?? '',
            role: initial?.role ?? USER_ROLES.ADMIN,
            isActive: initial?.isActive ?? true,
          },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Controller
        name="name"
        control={control}
        rules={{ required: 'Name is required' }}
        render={({ field, fieldState }) => (
          <Input {...field} label="Name" required error={fieldState.error?.message} />
        )}
      />
      <Controller
        name="username"
        control={control}
        rules={mode === 'create' ? { required: 'Username is required' } : {}}
        render={({ field, fieldState }) => (
          <Input {...field} label="Username" required={mode === 'create'} error={fieldState.error?.message} />
        )}
      />
      {mode === 'create' && (
        <Controller
          name="password"
          control={control}
          rules={{ required: 'Password is required' }}
          render={({ field, fieldState }) => (
            <Input {...field} type="password" label="Password" required error={fieldState.error?.message} />
          )}
        />
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={USER_ROLES.ADMIN}>Admin</option>
              <option value={USER_ROLES.DEVELOPER}>Developer</option>
              <option value={USER_ROLES.VIEWER}>Viewer</option>
              <option value={USER_ROLES.SUPER_ADMIN}>Super Admin</option>
            </select>
          )}
        />
      </div>

      {mode === 'edit' && (
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          )}
        />
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" isLoading={isSubmitting}>
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </div>
    </form>
  );
};